import { useState } from 'react'
import { Box, Typography, IconButton, CircularProgress, Tooltip } from '@mui/material'
import { PencilIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { deleteUserTemplate } from '../../../../../apiLayer/processHandlers/userTemplates'
import { QUERY_KEYS } from '../../../../../apiLayer/queryKeys'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import type { PurposeTemplate } from '../../../../../../../pure-js/types/PurposeTemplate'
import { RenameActivityTemplateDialog } from '../RenameActivityTemplateDialog/RenameActivityTemplateDialog'

const MAX_TEMPLATES = 10

// Chip background sits on top of heritageBlue container — use a slightly lighter shade
const CHIP_BG = INTERAX_COLORS.heritageBlue40
const CHIP_BORDER = INTERAX_COLORS.thunderGrey
const CHIP_HOVER = INTERAX_COLORS.thunderGrey20
const ICON_COLOR = INTERAX_COLORS.thunderGrey
const ICON_HOVER_COLOR = INTERAX_COLORS.white

interface Props {
  templates: PurposeTemplate[]
  onLoadTemplate: (template: PurposeTemplate) => void
}

export function PurposeTemplateChipStrip({ templates, onLoadTemplate }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<PurposeTemplate | null>(null)

  const atLimit = templates.length >= MAX_TEMPLATES

  async function handleConfirmDelete(templateId: string) {
    setDeletingId(templateId)
    setConfirmingDeleteId(null)
    try {
      await deleteUserTemplate(templateId)
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CURRENT_USER] })
    } catch {
      toast.error(t('common.errorGeneric'))
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: INTERAX_COLORS.white }}>
          {t('facilitator.sprintCreation.step2.templates.title')}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            px: 1.25,
            py: 0.5,
            borderRadius: '20px',
            bgcolor: atLimit ? INTERAX_COLORS.sun : INTERAX_COLORS.heritageBlue40,
            color: atLimit ? '#000' : INTERAX_COLORS.thunderGrey,
            border: `1px solid ${atLimit ? INTERAX_COLORS.sun : INTERAX_COLORS.thunderGrey}`,
            whiteSpace: 'nowrap'
          }}
        >
          {atLimit
            ? t('facilitator.sprintCreation.step2.templates.limitBadge')
            : t('facilitator.sprintCreation.step2.templates.counter', { count: templates.length })}
        </Typography>
      </Box>

      {/* Subtitle */}
      <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.thunderGrey, mb: 1.5 }}>
        {atLimit
          ? t('facilitator.sprintCreation.step2.templates.subtitleAtLimit')
          : t('facilitator.sprintCreation.step2.templates.subtitle')}
      </Typography>

      {/* Chip row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {templates.map((template) => {
          const isConfirming = confirmingDeleteId === template.id
          const isDeleting = deletingId === template.id

          return (
            <Box
              key={template.id}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                border: `1px solid ${CHIP_BORDER}`,
                borderRadius: '8px',
                overflow: 'hidden',
                bgcolor: CHIP_BG,
                opacity: isDeleting ? 0.5 : 1,
                transition: 'opacity 0.2s'
              }}
            >
              {isDeleting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75 }}>
                  <CircularProgress size={12} sx={{ mr: 1, color: INTERAX_COLORS.thunderGrey }} />
                  <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.white }}>{template.name}</Typography>
                </Box>
              ) : isConfirming ? (
                /* Confirm delete state */
                <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.5, gap: 0.5 }}>
                  <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.white, px: 0.5 }}>
                    {t('facilitator.sprintCreation.step2.templates.deleteConfirm.sure')}
                  </Typography>
                  <Tooltip title={t('facilitator.sprintCreation.step2.templates.deleteConfirm.confirm')}>
                    <IconButton
                      size="small"
                      aria-label={t('facilitator.sprintCreation.step2.templates.deleteConfirm.confirm')}
                      onClick={() => void handleConfirmDelete(template.id)}
                      sx={{
                        p: 0.5,
                        color: INTERAX_COLORS.sun,
                        '&:hover': { bgcolor: CHIP_HOVER }
                      }}
                    >
                      <CheckIcon style={{ width: 14, height: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('facilitator.sprintCreation.step2.templates.deleteConfirm.cancel')}>
                    <IconButton
                      size="small"
                      aria-label={t('facilitator.sprintCreation.step2.templates.deleteConfirm.cancel')}
                      onClick={() => setConfirmingDeleteId(null)}
                      sx={{
                        p: 0.5,
                        color: ICON_COLOR,
                        '&:hover': { color: ICON_HOVER_COLOR, bgcolor: CHIP_HOVER }
                      }}
                    >
                      <XMarkIcon style={{ width: 14, height: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                /* Normal state */
                <>
                  {/* Clickable name area */}
                  <Box
                    component="button"
                    onClick={() => onLoadTemplate(template)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.625,
                      border: 'none',
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: CHIP_HOVER },
                      transition: 'background-color 0.15s'
                    }}
                  >
                    {/* Amber dot */}
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: INTERAX_COLORS.sun,
                        flexShrink: 0
                      }}
                    />
                    <Typography
                      sx={{ fontSize: '13px', fontWeight: 600, color: INTERAX_COLORS.white, whiteSpace: 'nowrap' }}
                    >
                      {template.name}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: INTERAX_COLORS.thunderGrey, whiteSpace: 'nowrap' }}>
                      ·{' '}
                      {t('facilitator.sprintCreation.step2.templates.activityCount', {
                        count: template.activities.length
                      })}
                    </Typography>
                  </Box>

                  {/* Vertical divider */}
                  <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: CHIP_BORDER }} />

                  {/* Rename button */}
                  <Tooltip title={t('facilitator.sprintCreation.step2.templates.renameDialog.title')}>
                    <IconButton
                      size="small"
                      aria-label={t('facilitator.sprintCreation.step2.templates.renameDialog.title')}
                      onClick={() => setRenaming(template)}
                      sx={{
                        borderRadius: 0,
                        px: 0.75,
                        py: 0.625,
                        color: ICON_COLOR,
                        '&:hover': { color: ICON_HOVER_COLOR, bgcolor: CHIP_HOVER }
                      }}
                    >
                      <PencilIcon style={{ width: 14, height: 14 }} />
                    </IconButton>
                  </Tooltip>

                  {/* Vertical divider */}
                  <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: CHIP_BORDER }} />

                  {/* Delete button */}
                  <IconButton
                    size="small"
                    aria-label={t('facilitator.sprintCreation.step2.templates.deleteButton')}
                    onClick={() => setConfirmingDeleteId(template.id)}
                    sx={{
                      borderRadius: 0,
                      px: 0.75,
                      py: 0.625,
                      color: ICON_COLOR,
                      '&:hover': { color: ICON_HOVER_COLOR, bgcolor: CHIP_HOVER }
                    }}
                  >
                    <XMarkIcon style={{ width: 14, height: 14 }} />
                  </IconButton>
                </>
              )}
            </Box>
          )
        })}
      </Box>

      {/* Rename dialog */}
      {renaming && (
        <RenameActivityTemplateDialog
          open={true}
          templateId={renaming.id}
          currentName={renaming.name}
          onClose={() => setRenaming(null)}
        />
      )}
    </Box>
  )
}
